<script setup>
/**
 * The six illustrations a policy line can wear (`ICONS`), and the portal nav's
 * calendar and envelope: all of them the prototype's one outline set (`ic`), so
 * the nav draws from here too (XN-4736).
 *
 * One component with a path map rather than six icon files: they are only ever
 * drawn as a set — the editor shows all six side by side and the card shows the
 * chosen one — so they have to share a weight and a grid to read as a set, and
 * six separate files is six chances for that to drift. The sidebar's house and
 * shield are its own (shell/HubNavIcon), at a different weight, and are
 * deliberately left alone.
 */
import { computed } from 'vue';

import { DEFAULT_ICON } from '../../helpers/policyLines.js';

const props = defineProps({
  name: { type: String, default: DEFAULT_ICON },
  size: { type: [Number, String], default: 20 },
});

// Each icon is a list of SVG shapes, rendered as elements rather than as a
// markup string: nothing here goes through innerHTML, so there is no injection
// surface to reason about even though the map is closed.
const SHAPES = {
  home: [
    { tag: 'path', attrs: { d: 'M3 10.5 12 3l9 7.5' } },
    { tag: 'path', attrs: { d: 'M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5' } },
  ],
  door: [
    { tag: 'path', attrs: { d: 'M4 21V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v17' } },
    { tag: 'path', attrs: { d: 'M3 21h18M11.5 12.5v1' } },
  ],
  book: [
    { tag: 'path', attrs: { d: 'M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2z' } },
    { tag: 'path', attrs: { d: 'M8 7h7' } },
  ],
  shield: [
    { tag: 'path', attrs: { d: 'M12 3 5 6v5.5c0 4.4 3 8 7 9.5 4-1.5 7-5.1 7-9.5V6z' } },
    { tag: 'path', attrs: { d: 'm9.5 12 1.8 1.8L15 10' } },
  ],
  gift: [
    { tag: 'rect', attrs: { x: 3, y: 8, width: 18, height: 13, rx: 2 } },
    { tag: 'path', attrs: { d: 'M3 12h18M12 8v13' } },
    { tag: 'path', attrs: { d: 'M12 8S10.5 3.5 8 4.5 9 8 12 8s5-2.5 3.5-3.5S12 8 12 8' } },
  ],
  star: [
    { tag: 'path', attrs: { d: 'm12 3.5 2.6 5.6 6 .8-4.4 4.2 1.1 6L12 17.3 6.7 20.1l1.1-6L3.4 9.9l6-.8z' } },
  ],
  // The nav's only; not in ICONS, so a policy line cannot pick them.
  cal: [
    { tag: 'rect', attrs: { x: 3, y: 5, width: 18, height: 16, rx: 2 } },
    { tag: 'path', attrs: { d: 'M8 3v4M16 3v4M3 10h18' } },
  ],
  mail: [
    { tag: 'rect', attrs: { x: 3, y: 5, width: 18, height: 14, rx: 2 } },
    { tag: 'path', attrs: { d: 'm3.5 7 8.5 6 8.5-6' } },
  ],
};

// An unknown name falls back rather than rendering nothing: a line saved with an
// icon a later release removed should still draw as something.
const shapes = computed(() => SHAPES[props.name] ?? SHAPES[DEFAULT_ICON]);
</script>

<template>
  <svg
    :width="props.size"
    :height="props.size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <component :is="shape.tag" v-for="(shape, index) in shapes" :key="index" v-bind="shape.attrs" />
  </svg>
</template>
